// Annual review as a case: six steps from pack to a rolled review date. Pure,
// so the client record, the book grid and the API agree on progress.
import { addMonthsIso } from './bookEdit.js';

export const REVIEW_STEP_KEYS = ['pack', 'meeting', 'notes', 'actions', 'roa', 'rolled'] as const;
export type ReviewStepKey = (typeof REVIEW_STEP_KEYS)[number];

export const REVIEW_STEP_LABEL: Record<ReviewStepKey, string> = {
  pack: 'Pack ready',
  meeting: 'Meeting booked',
  notes: 'Notes captured',
  actions: 'Actions agreed',
  roa: 'RoA produced',
  rolled: 'Review date rolled',
};

export interface ReviewAction {
  id: string; text: string; owner: string; due: string | null; done: boolean;
  /** When the action was sent to Next-Task.ai as a task (null/absent = not sent). */
  taskSentAt?: string | null;
}

export interface ReviewCaseRecord {
  openedAt: string;          // ISO timestamp
  dueDate: string | null;
  packReadyAt: string | null;
  meetingDate: string | null;
  actions: ReviewAction[];
  noActions: boolean;
  roaNotRequired: string | null;
  closedAt: string | null;
  nextReviewDue: string | null;
}

/** What the record shows since the case opened (found by the server, not typed in). */
export interface ReviewEvidence {
  /** Latest meeting file note on or after the case opened. */
  meetingNoteAt: string | null;
  /** Latest Record of Advice created on or after the case opened. */
  roaAt: string | null;
}

export interface ReviewStep { key: ReviewStepKey; label: string; done: boolean; detail: string | null }

const d = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

export function reviewSteps(c: ReviewCaseRecord, ev: ReviewEvidence): ReviewStep[] {
  const openActions = c.actions.filter((a) => !a.done).length;
  const step = (key: ReviewStepKey, done: boolean, detail: string | null): ReviewStep => ({ key, label: REVIEW_STEP_LABEL[key], done, detail });
  return [
    step('pack', !!c.packReadyAt, d(c.packReadyAt)),
    step('meeting', !!c.meetingDate, c.meetingDate),
    step('notes', !!ev.meetingNoteAt, d(ev.meetingNoteAt)),
    step('actions', c.noActions || c.actions.length > 0,
      c.noActions ? 'No actions' : c.actions.length ? `${c.actions.length} action${c.actions.length === 1 ? '' : 's'}${openActions ? `, ${openActions} open` : ''}` : null),
    step('roa', !!ev.roaAt || !!c.roaNotRequired, ev.roaAt ? d(ev.roaAt) : c.roaNotRequired ? `Not required: ${c.roaNotRequired}` : null),
    step('rolled', !!c.closedAt, c.nextReviewDue ? `Next review ${c.nextReviewDue}` : null),
  ];
}

/** Grid label, e.g. "3/6 Actions agreed" (the next step to do), or "Done". */
export function reviewStageLabel(steps: ReviewStep[]): string {
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done);
  return next ? `${done}/${steps.length} ${next.label}` : 'Done';
}

/** Steps that must be done before the case can close and roll the date. */
export const REQUIRED_TO_CLOSE: ReviewStepKey[] = ['notes', 'actions', 'roa'];

export function missingToClose(steps: ReviewStep[]): ReviewStep[] {
  return steps.filter((s) => REQUIRED_TO_CLOSE.includes(s.key) && !s.done);
}

/**
 * Next review date when a case closes: a year from the meeting (or from the
 * close date when no meeting date was recorded), so the cycle follows when the
 * review actually happened.
 */
export function rolledReviewDue(meetingDate: string | null, closeDate: string, months = 12): string {
  return addMonthsIso(meetingDate ?? closeDate, months);
}

/** An open action from an open review case, for the Today list and the morning email. */
export interface ReviewActionItem {
  clientId: string;
  caseId: string;
  actionId: string;
  text: string;
  owner: string;
  due: string | null;
  /** Days past due (0 = due today); null when not yet due or undated. */
  overdueDays: number | null;
}

/**
 * Open actions on open review cases that need attention: overdue, due within
 * `withinDays`, or undated (an action nobody has put a date on is a risk of its
 * own). Done actions, closed cases and actions dated further out are left off.
 */
export function openReviewActionItems(
  cases: Array<{ id: string; clientId: string; closedAt: string | Date | null; actions: ReviewAction[] }>,
  today: string,
  withinDays = 7,
): ReviewActionItem[] {
  const horizon = new Date(Date.parse(`${today}T00:00:00Z`) + withinDays * 86_400_000).toISOString().slice(0, 10);
  const out: ReviewActionItem[] = [];
  for (const c of cases) {
    if (c.closedAt) continue;
    for (const a of c.actions ?? []) {
      if (a.done) continue;
      const due = a.due ? a.due.slice(0, 10) : null;
      if (due && due > horizon) continue;
      const overdueDays = due && due <= today
        ? Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86_400_000)
        : null;
      out.push({ clientId: c.clientId, caseId: c.id, actionId: a.id, text: a.text, owner: a.owner, due, overdueDays });
    }
  }
  return out;
}
